import { Handshake, Landmark, Building2, ShieldCheck, ScrollText, Compass } from "lucide-react";
import Reveal, { Stagger, StaggerItem } from "@/components/motion/Reveal";

const PIPELINE = [
  {
    step: "01",
    title: "Landowner Engagement",
    desc: "Direct, private negotiations with individual landholders across rural and peri-urban Karnataka.",
    icon: Handshake,
  },
  {
    step: "02",
    title: "Legal & Title Diligence",
    desc: "Multi-generational title verification, encumbrance clearance, and revenue-record consolidation.",
    icon: ScrollText,
  },
  {
    step: "03",
    title: "Strategic Aggregation",
    desc: "Contiguous parcel assembly — engineered to meet the exact footprint of Government or MNC mandates.",
    icon: Compass,
  },
  {
    step: "04",
    title: "Institutional Disposition",
    desc: "Clean, single-instrument transfer to Government bodies and multinational corporations.",
    icon: Landmark,
  },
];

export default function BusinessModel() {
  return (
    <section
      id="business"
      data-testid="business-model-section"
      className="relative bg-[#0A0A0D] py-32 md:py-48 overflow-hidden"
    >
      {/* Decorative side image */}
      <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-1/3 opacity-25 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1679364297777-1db77b6199be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4MzQzMzU2Nnww&ixlib=rb-4.1.0&q=85"
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#0A0A0D]/70 to-[#0A0A0D]" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <Reveal className="md:col-span-5">
            <div className="overline mb-6">Our Businesses · I</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-neutral-50">
              Land, acquired <span className="italic text-[#D4AF37]">privately.</span>
              <br />
              Delivered <span className="italic">institutionally.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6 md:col-start-7 md:pt-6">
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed">
              RUDRA EMPIRE occupies a rare seat in Karnataka’s land economy — a
              private house that speaks the language of the villager as
              fluently as it speaks the language of the boardroom. Our work
              begins on the soil and ends at the sub-registrar; every step in
              between is bound by discretion, documentation and duty.
            </p>
          </Reveal>
        </div>

        {/* Pipeline grid — numbered manifesto */}
        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" gap={0.12}>
          {PIPELINE.map(({ step, title, desc, icon: Icon }) => (
            <StaggerItem key={step}>
              <div
                data-testid={`pipeline-step-${step}`}
                className="card-gold-hover bg-[#121215] p-8 relative h-full"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="font-display italic text-5xl text-[#D4AF37]/80">
                    {step}
                  </div>
                  <Icon strokeWidth={1} className="w-8 h-8 text-neutral-500" />
                </div>
                <div className="font-display text-2xl text-neutral-50 leading-tight mb-4">
                  {title}
                </div>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  {desc}
                </p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Client bands */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-white/8 p-10 md:p-12 bg-[#121215]">
            <Landmark strokeWidth={1} className="w-8 h-8 text-[#D4AF37] mb-6" />
            <div className="overline mb-3">For Government</div>
            <div className="font-display text-3xl md:text-4xl text-neutral-50 leading-tight">
              Land ready for the public interest.
            </div>
            <p className="mt-5 text-neutral-400 leading-relaxed">
              Highways, industrial corridors, SEZs, PSU expansions — we deliver
              titled, contiguous parcels prepared for public procurement
              protocols and Karnataka land-acquisition frameworks.
            </p>
          </div>
          <div className="border border-white/8 p-10 md:p-12 bg-[#121215]">
            <Building2 strokeWidth={1} className="w-8 h-8 text-[#D4AF37] mb-6" />
            <div className="overline mb-3">For Multinationals</div>
            <div className="font-display text-3xl md:text-4xl text-neutral-50 leading-tight">
              Sites that are shovel-ready.
            </div>
            <p className="mt-5 text-neutral-400 leading-relaxed">
              Manufacturing campuses, data centres, logistics parks and R&amp;D
              headquarters — we bring MNCs land they can build on without
              inheriting local complexity.
            </p>
          </div>
        </div>

        {/* Trust bar */}
        <div className="mt-16 flex flex-wrap items-center gap-x-10 gap-y-4 text-neutral-500">
          <ShieldCheck strokeWidth={1} className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-xs tracking-[0.24em] uppercase">
            Discreet aggregation
          </span>
          <span className="text-neutral-800">/</span>
          <span className="text-xs tracking-[0.24em] uppercase">
            Single-instrument transfer
          </span>
          <span className="text-neutral-800">/</span>
          <span className="text-xs tracking-[0.24em] uppercase">
            30 years, one mandate
          </span>
        </div>
      </div>
    </section>
  );
}
